import React, { useState, useEffect } from 'react';
import { Button, Space, message, Typography, Input, Card, Checkbox, Tooltip, Tabs } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, EyeOutlined, PlusOutlined, QuestionCircleOutlined, SettingOutlined, RobotOutlined, FormOutlined } from '@ant-design/icons';
import AIGeneratingOverlay from '../components/WorkflowDesigner/components/AIGeneratingOverlay';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  generateMetaFlowJson, 
  parseMetaFlowJson, 
  validateMetaFlowJson,
  getDefaultScreen,
  getDefaultComponent,
  createMetaFlowRequest,
  createMetaFlowUpdateRequest,
  isSameFlowAction
} from '../utils/metaFlowUtils';
import ComponentPalette from '../components/MetaFlowBuilder/ComponentPalette';
import ScreenEditor from '../components/MetaFlowBuilder/ScreenEditor';
import ComponentPropertyEditor from '../components/MetaFlowBuilder/ComponentPropertyEditor';
import MetaFlowFieldDisplaySetting from '../components/MetaFlowBuilder/MetaFlowFieldDisplaySetting';
import MetaFlowBuilderAIChat from '../components/MetaFlowBuilder/MetaFlowBuilderAIChat';

const { Title } = Typography;
const { TextArea } = Input;

// 添加紫色返回按鈕的 hover 樣式
const purpleButtonStyle = `
  .purple-back-button:hover {
    background-color: #8c4dd4 !important;
    border-color: #8c4dd4 !important;
  }
`;

const MetaFlowBuilder = ({ initialSchema, onSave, onBack }) => {
  const { t } = useLanguage();
  
  // 將組件 type 映射到翻譯鍵
  const getComponentLabel = (componentType) => {
    const typeToLabelKey = {
      'text_input': 'textInput',
      'text_area': 'textArea',
      'date_picker': 'datePicker',
      'calendar_picker': 'calendarPicker',
      'select': 'select',
      'checkbox': 'checkbox',
      'radio': 'radio',
      'chips_selector': 'chipsSelector',
      'image': 'image',
      'image_carousel': 'imageCarousel',
      'photo_picker': 'photoPicker',
      'document_picker': 'documentPicker',
      'embedded_link': 'embeddedLink',
      'opt_in': 'optIn',
      'if': 'if',
      'switch': 'switch',
      'navigation_list': 'navigationList',
      'rich_text': 'richText'
    };
    const labelKey = typeToLabelKey[componentType];
    if (labelKey) {
      return t(`metaFlowBuilder.componentPalette.componentLabels.${labelKey}`);
    }
    return componentType; // 如果沒有對應的翻譯鍵，返回原始 type
  };
  
  // 檢查 title 是否為默認值（硬編碼的中文）
  const isDefaultTitle = (title, componentType) => {
    const defaultTitles = {
      'text_input': '文字輸入',
      'text_area': '多行文字',
      'date_picker': '日期選擇',
      'calendar_picker': '日曆選擇',
      'time_picker': '時間選擇',
      'select': '下拉選擇',
      'checkbox': '複選框組',
      'radio': '單選框組',
      'chips_selector': '小標籤選擇器',
      'image': '圖片',
      'image_carousel': '圖片輪播',
      'photo_picker': '照片選擇器',
      'document_picker': '文檔選擇器',
      'embedded_link': '嵌入式鏈接',
      'opt_in': '選擇加入',
      'if': '條件判斷 (If)',
      'switch': '條件渲染 (Switch)',
      'navigation_list': '導航列表',
      'rich_text': '富文本顯示'
    };
    return title === defaultTitles[componentType];
  };
  
  // 獲取組件顯示標題（如果 title 是默認值，使用翻譯；否則使用用戶自定義的 title）
  const getComponentDisplayTitle = (component) => {
    if (component.title && !isDefaultTitle(component.title, component.type)) {
      // 如果 title 存在且不是默認值，使用用戶自定義的 title
      return component.title;
    }
    // 否則使用翻譯後的標籤
    return getComponentLabel(component.type);
  };
  
  // 獲取 Screen 顯示標題（如果 title 是默認值，使用翻譯；否則使用用戶自定義的 title）
  const getScreenDisplayTitle = (screen) => {
    const defaultScreenTitle = t('metaFlowBuilder.page.defaultScreenTitle');
    if (screen.title && screen.title !== '新 Screen' && screen.title !== defaultScreenTitle) {
      // 如果 title 存在且不是默認值，使用用戶自定義的 title
      return screen.title;
    }
    // 否則使用翻譯後的標籤
    return defaultScreenTitle;
  };
  
  // 基本狀態
  const [flowName, setFlowName] = useState(initialSchema?.name || t('metaFlowBuilder.page.defaultFlowName'));
  const [flowDescription, setFlowDescription] = useState(initialSchema?.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(true); // 是否保存為 Flow Template（默認勾選）
  
  // Flow 數據狀態
  const [flowData, setFlowData] = useState({
    name: initialSchema?.name || t('metaFlowBuilder.page.defaultFlowName'),
    categories: ['LEAD_GENERATION'],
    screens: initialSchema?.metaFlowJson ? 
      parseMetaFlowJson(initialSchema.metaFlowJson).screens : 
      [getDefaultScreen()]
  });
  
  // 編輯相關狀態
  const [isEditing, setIsEditing] = useState(!!initialSchema?.id);
  const [formId, setFormId] = useState(initialSchema?.id || null);
  const [metaFlowId, setMetaFlowId] = useState(initialSchema?.metaFlowId || null);
  
  // 選中狀態
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);

  // 字段顯示設定相關狀態（與 EFormDesigner 共用 eFormDefinition.field_display_settings 欄位）
  const [fieldDisplaySettingVisible, setFieldDisplaySettingVisible] = useState(false);
  const [fieldDisplaySettings, setFieldDisplaySettings] = useState([]);
  
  // 當 flowData.screens 更新時，如果沒有選中的 screen，自動選中第一個
  useEffect(() => {
    if (flowData.screens && flowData.screens.length > 0 && !selectedScreen) {
      console.log('🔄 自動選中第一個 Screen（因為沒有選中的 screen）');
      setSelectedScreen(flowData.screens[0]);
    }
  }, [flowData.screens, selectedScreen]);
  
  // JSON 預覽狀態
  const [jsonPreviewVisible, setJsonPreviewVisible] = useState(false);

  // AI 表單助理
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [leftPanelTab, setLeftPanelTab] = useState('formInfo');
  
  // 側邊欄收合狀態
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // 監聽側邊欄收合狀態
  useEffect(() => {
    const checkSidebarState = () => {
      const sider = document.querySelector('.ant-layout-sider');
      if (sider) {
        const isCollapsed = sider.classList.contains('ant-layout-sider-collapsed');
        setSidebarCollapsed(isCollapsed);
      }
    };
    
    // 初始檢查
    checkSidebarState();
    
    // 監聽 DOM 變化
    const observer = new MutationObserver(checkSidebarState);
    const sider = document.querySelector('.ant-layout-sider');
    if (sider) {
      observer.observe(sider, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
    
    // 監聽窗口大小變化
    window.addEventListener('resize', checkSidebarState);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkSidebarState);
    };
  }, []);
  
  // ── Template 訊息專用文字 / 範例值的 merge 工具 ──
  // metaFlowJson 由 Meta API 來回會剝離自訂欄位，故 Template 訊息文字 / 範例值另存於 eFormDefinition 獨立欄位。
  // 此函式把那些獨立欄位 merge 回 parseMetaFlowJson() 的 screens 結構（只放回第一屏，因為 Template 只渲染第一屏）。
  // 兩條載入路徑（initialSchema props / loadFormContent fetch）共用此邏輯，避免遺漏。
  const mergeTemplateOverridesIntoScreens = (parsedScreens, source) => {
    if (!Array.isArray(parsedScreens) || parsedScreens.length === 0) return parsedScreens || [];
    let examplesByScreen = {};
    if (source?.metaTemplateBodyExamples) {
      try {
        const raw = typeof source.metaTemplateBodyExamples === 'string'
          ? JSON.parse(source.metaTemplateBodyExamples)
          : source.metaTemplateBodyExamples;
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
          examplesByScreen = raw;
        }
      } catch (e) {
        console.warn('⚠️ 解析 metaTemplateBodyExamples 失敗，忽略:', e?.message);
      }
    }
    const firstScreenId = parsedScreens[0].id;
    return parsedScreens.map((s) => {
      const arr = examplesByScreen[s.id];
      const isFirst = s.id === firstScreenId;
      const bodyOverlay = { ...(s.data?.body || { type: 'body', text: '' }) };
      if (Array.isArray(arr)) {
        bodyOverlay.metaTemplateExamples = arr.map(v => (typeof v === 'string' ? v : ''));
      }
      if (isFirst && typeof source?.metaTemplateBodyText === 'string' && source.metaTemplateBodyText.length > 0) {
        bodyOverlay.metaTemplateBodyText = source.metaTemplateBodyText;
      }
      // Header 只在 source 已有 header 物件、或第一屏需要 Template Header 時建立 overlay；
      // 沒有 header 物件就保持原樣（null/undefined）以維持「無 Header」狀態。
      let headerOverlay = s.data?.header;
      if (isFirst && (typeof source?.metaTemplateHeaderText === 'string' && source.metaTemplateHeaderText.length > 0
                       || typeof source?.metaTemplateHeaderExample === 'string' && source.metaTemplateHeaderExample.length > 0)) {
        headerOverlay = {
          ...(s.data?.header || { type: 'header', format: 'TEXT' })
        };
        if (typeof source.metaTemplateHeaderText === 'string' && source.metaTemplateHeaderText.length > 0) {
          headerOverlay.metaTemplateHeaderText = source.metaTemplateHeaderText;
        }
        if (typeof source.metaTemplateHeaderExample === 'string' && source.metaTemplateHeaderExample.length > 0) {
          headerOverlay.metaTemplateHeaderExample = source.metaTemplateHeaderExample;
        }
      }
      return {
        ...s,
        data: {
          ...(s.data || {}),
          body: bodyOverlay,
          header: headerOverlay
        }
      };
    });
  };

  // 載入表單內容
  useEffect(() => {
    if (initialSchema?.id && initialSchema?.formType === 'MetaFlows') {
      setIsLoading(true);
      
      // 如果 initialSchema 已經包含 metaFlowJson，直接使用
      if (initialSchema.metaFlowJson) {
        try {
          console.log('📥 從 initialSchema 載入 metaFlowJson:', initialSchema.metaFlowJson);
          const parsed = parseMetaFlowJson(initialSchema.metaFlowJson);
          console.log('📥 解析後的數據:', parsed);
          console.log('📥 Screens 數量:', parsed.screens?.length || 0);

          // ✅ 把 Template 訊息專用文字 / 範例值 merge 回第一屏（initialSchema 來自 list 端點，已含這些欄位）
          const screensMerged = mergeTemplateOverridesIntoScreens(parsed.screens || [], initialSchema);

          // 確保 parsed 包含 name（優先使用表單的 name，如果 JSON 中沒有）
          const parsedWithName = {
            ...parsed,
            screens: screensMerged,
            name: parsed.name || initialSchema.name || t('metaFlowBuilder.page.defaultFlowName')
          };
          setFlowData(parsedWithName);
          setFlowName(parsedWithName.name);
          setFlowDescription(initialSchema.description || '');
          if (initialSchema.metaFlowId) {
            setMetaFlowId(initialSchema.metaFlowId);
          }
          setIsEditing(true);
          setFormId(initialSchema.id);

          // 載入字段顯示設定（與 EFormDesigner 共用 eFormDefinition.field_display_settings）
          if (initialSchema.fieldDisplaySettings) {
            try {
              const settings = typeof initialSchema.fieldDisplaySettings === 'string'
                ? JSON.parse(initialSchema.fieldDisplaySettings)
                : initialSchema.fieldDisplaySettings;
              setFieldDisplaySettings(Array.isArray(settings) ? settings : []);
            } catch (e) {
              console.warn('從 initialSchema 解析 fieldDisplaySettings 失敗:', e?.message);
              setFieldDisplaySettings([]);
            }
          }

          // 如果有 screens，自動選中第一個
          if (parsedWithName.screens && parsedWithName.screens.length > 0) {
            console.log('📥 自動選中第一個 Screen:', parsedWithName.screens[0].id);
            setSelectedScreen(parsedWithName.screens[0]);
          }
          
          setIsLoading(false);
          message.success('表單內容載入成功！');
          return;
        } catch (error) {
          console.error('解析 initialSchema 的 metaFlowJson 失敗:', error);
          // 如果解析失敗，繼續從 API 加載
        }
      }
      
      // 否則從 API 加載
      loadFormContent(initialSchema.id);
    } else if (initialSchema?.formType === 'MetaFlows' && initialSchema?.metaFlowJson) {
      // 新表單但有初始 JSON（不應該發生，但處理一下）
      try {
        const parsed = parseMetaFlowJson(initialSchema.metaFlowJson);
        setFlowData(parsed);
        setFlowName(parsed.name || initialSchema.name);
        // 如果有 screens，自動選中第一個
        if (parsed.screens && parsed.screens.length > 0) {
          setSelectedScreen(parsed.screens[0]);
        }
      } catch (error) {
        console.error('解析初始 metaFlowJson 失敗:', error);
      }
    }
  }, [initialSchema?.id, initialSchema?.metaFlowJson]);
  
  // 載入表單內容的函數
  const loadFormContent = async (formId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('請先登入');
        return;
      }

      // 從數據庫獲取（不使用 fromApi，因為我們要使用保存的 JSON，而不是 Meta API 的響應）
      const response = await fetch(`/api/eforms/${formId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const formData = await response.json();
      console.log('📥 載入的表單數據:', formData);
      console.log('📥 metaFlowJson 類型:', typeof formData.metaFlowJson);
      console.log('📥 metaFlowJson 內容:', formData.metaFlowJson);

      // 更新表單內容
      if (formData.metaFlowJson) {
        try {
          const parsed = parseMetaFlowJson(formData.metaFlowJson);
          console.log('📥 解析後的 Flow 數據:', parsed);
          console.log('📥 Screens 數量:', parsed.screens?.length || 0);
          if (parsed.screens && parsed.screens.length > 0) {
            console.log('📥 第一個 Screen:', JSON.stringify(parsed.screens[0], null, 2));
          }

          // ✅ 把 Template 訊息專用文字 / 範例值 merge 回第一屏。詳見 mergeTemplateOverridesIntoScreens 註解。
          const screensWithExamples = mergeTemplateOverridesIntoScreens(parsed.screens || [], formData);

          // 確保 parsed 包含 name（優先使用表單的 name，如果 JSON 中沒有）
          const parsedWithName = {
            ...parsed,
            screens: screensWithExamples,
            name: parsed.name || formData.name || t('metaFlowBuilder.page.defaultFlowName')
          };
          setFlowData(parsedWithName);
          setFlowName(parsedWithName.name);
          
          // 如果有 screens，選中第一個
          if (parsedWithName.screens && parsedWithName.screens.length > 0) {
            setSelectedScreen(parsedWithName.screens[0]);
          }
        } catch (error) {
          console.error('❌ 解析 Meta Flow JSON 失敗:', error);
          console.error('❌ 錯誤詳情:', error.message);
          console.error('❌ 原始 JSON:', formData.metaFlowJson);
          message.error('解析 Flow JSON 失敗: ' + error.message);
        }
      } else {
        console.warn('⚠️ 表單數據中沒有 metaFlowJson');
      }
      
      if (formData.name) {
        setFlowName(formData.name);
      }
      
      if (formData.description) {
        setFlowDescription(formData.description);
      }
      
      if (formData.metaFlowId) {
        setMetaFlowId(formData.metaFlowId);
      }

      // 載入字段顯示設定（與 EFormDesigner 共用 eFormDefinition.field_display_settings）
      if (formData.fieldDisplaySettings) {
        try {
          const settings = typeof formData.fieldDisplaySettings === 'string'
            ? JSON.parse(formData.fieldDisplaySettings)
            : formData.fieldDisplaySettings;
          setFieldDisplaySettings(Array.isArray(settings) ? settings : []);
        } catch (e) {
          console.warn('解析 fieldDisplaySettings 失敗:', e?.message);
          setFieldDisplaySettings([]);
        }
      } else {
        setFieldDisplaySettings([]);
      }

      setIsEditing(true);
      setFormId(formData.id);

      message.success('表單內容載入成功！');
    } catch (error) {
      console.error('❌ 載入表單內容失敗:', error);
      message.error('載入表單內容失敗: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 處理字段顯示設定保存
  // 注意：與 EFormDesigner 不同，這裡 *不* 自動發送 PUT 立即落盤。原因：
  //   EFormDefinitionsController.PUT 對 MetaFlows 類型會無條件覆寫
  //   MetaTemplateBodyExamples / MetaTemplateBodyText / MetaTemplateHeaderText / Status 等欄位，
  //   partial PUT 會誤把這些設成 null，且若帶 MetaFlowJson 還會觸發一次 Meta API 更新。
  //   因此這裡只更新前端 state，由使用者按頂部「保存」一併送出（handleSave payload 已含 fieldDisplaySettings）。
  const handleFieldDisplaySettingSave = async (settings) => {
    setFieldDisplaySettings(settings);
    // 提示使用者後續仍需按「保存」整個 Flow 才會落盤
    if (isEditing && formId) {
      message.info(t('metaFlowBuilder.page.messages.fieldDisplaySettingPendingSave') || '字段設定已暫存，請按「保存」按鈕讓變更生效');
    }
  };

  // 開啟字段顯示設定 modal
  const openFieldDisplaySetting = () => {
    const hasAnyInputComponent = (flowData.screens || []).some(s => (s.data?.actions || []).length > 0);
    if (!hasAnyInputComponent) {
      message.warning(t('metaFlowBuilder.page.messages.noFieldsToConfigure') || '請先設計 Flow 並加入輸入組件');
      return;
    }
    setFieldDisplaySettingVisible(true);
  };

  // 保存表單
  const handleSave = async () => {
    try {
      // 確保有 Flow 名稱
      if (!flowName || flowName.trim() === '') {
        message.error(t('metaFlowBuilder.page.messages.flowNameRequired'));
        return;
      }

      // flowData 為唯一真相來源（handleUpdateScreen 已同步）；勿用可能過期的 selectedScreen 覆蓋整屏
      let finalFlowData = {
        ...flowData,
        name: flowName || flowData.name || t('metaFlowBuilder.page.defaultFlowName')
      };

      setIsSaving(true);

      // 生成 Meta Flow JSON - 使用最新的數據（已經包含 name）
      // 注意：Meta API 的 JSON body 不應該包含 name 和 categories（它們是 API 參數）
      // 但後端需要這些字段來構建 MetaFlowCreateRequest，所以我們在 JSON 中包含它們
      // 後端會正確處理這些字段
      const metaFlowJson = generateMetaFlowJson(finalFlowData);
      
      // 驗證 Flow 數據
      const validation = validateMetaFlowJson(metaFlowJson);
      if (!validation.valid) {
        setIsSaving(false);
        console.error('❌ Flow JSON 驗證失敗:');
        validation.errors.forEach((error, index) => {
          console.error(`   ${index + 1}. ${error}`);
        });
        message.error({
          content: `Flow 數據驗證失敗:\n${validation.errors.map((e, i) => `${i + 1}. ${e}`).join('\n')}`,
          duration: 15, // 顯示 15 秒
          style: { whiteSpace: 'pre-wrap', maxWidth: '600px' }
        });
        return;
      }
      
      console.log('✅ Flow JSON 驗證通過');
      
      // 添加 name 和 categories 到 JSON（後端需要）
      const fullMetaFlowJson = {
        ...metaFlowJson,
        name: flowName || finalFlowData.name || t('metaFlowBuilder.page.defaultFlowName'),
        categories: finalFlowData.categories || ['LEAD_GENERATION']
      };

      console.log('📤 準備保存的 Flow 數據:', finalFlowData);
      console.log('📤 生成的 Meta Flow JSON:', fullMetaFlowJson);
      console.log('📤 Screens 數量:', fullMetaFlowJson.screens?.length || 0);
      if (fullMetaFlowJson.screens && fullMetaFlowJson.screens.length > 0) {
        console.log('📤 第一個 Screen:', JSON.stringify(fullMetaFlowJson.screens[0], null, 2));
      }

      // 從各 screen.data.body.metaTemplateExamples 抽出，序列化為「以 screen.id 為 key」的物件，
      // 寫到 eFormDefinition.MetaTemplateBodyExamples（獨立欄位，避免被 Meta API 來回剝離）。
      // 沒有任何 placeholder 範例時送 null，後端會 fallback 為「範例值N」自動值。
      const bodyExamplesByScreen = {};
      (finalFlowData.screens || []).forEach((s) => {
        const arr = s?.data?.body?.metaTemplateExamples;
        if (Array.isArray(arr) && arr.some(v => typeof v === 'string' && v.trim() !== '')) {
          bodyExamplesByScreen[s.id] = arr.map(v => (typeof v === 'string' ? v : ''));
        }
      });
      const metaTemplateBodyExamplesJson = Object.keys(bodyExamplesByScreen).length > 0
        ? JSON.stringify(bodyExamplesByScreen)
        : null;

      // Template 訊息專用文字（只取第一屏，因為 Template 只渲染 Flow 第一屏的 BODY/HEADER）。
      // 為空字串時送 null，BE 會 fallback 為從 FlowJson 抽取（向後相容舊表單）。
      const firstScreen = finalFlowData.screens?.[0];
      const pickNonEmpty = (v) => (typeof v === 'string' && v.trim() !== '' ? v : null);
      const metaTemplateBodyText = pickNonEmpty(firstScreen?.data?.body?.metaTemplateBodyText);
      const metaTemplateHeaderText = pickNonEmpty(firstScreen?.data?.header?.metaTemplateHeaderText);
      const metaTemplateHeaderExample = pickNonEmpty(firstScreen?.data?.header?.metaTemplateHeaderExample);

      const formData = {
        name: flowName,
        description: flowDescription,
        formType: 'MetaFlows',
        metaFlowJson: JSON.stringify(fullMetaFlowJson),
        metaTemplateBodyExamples: metaTemplateBodyExamplesJson,
        metaTemplateBodyText: metaTemplateBodyText,
        metaTemplateHeaderText: metaTemplateHeaderText,
        metaTemplateHeaderExample: metaTemplateHeaderExample,
        // 字段顯示設定（與 EFormDesigner 共用 eFormDefinition.field_display_settings 欄位）。
        // 後端 EFormInstancesController.ExtractFieldValues 會用這個來決定 PendingTasks 展開列要顯示哪些欄位。
        fieldDisplaySettings: fieldDisplaySettings.length > 0 ? JSON.stringify(fieldDisplaySettings) : null,
        status: 'A',
        RStatus: 'A'
      };

      // 如果是編輯模式，添加 updatedAt
      if (isEditing) {
        formData.updatedAt = new Date().toISOString();
      }

      console.log('📤 發送保存請求:', {
        ...formData,
        metaFlowJson: metaFlowJson // 顯示解析後的 JSON，而不是字符串
      });

      const token = localStorage.getItem('token');
      const url = isEditing ? `/api/eforms/${formId}` : '/api/eforms';
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      console.log('📥 收到響應:', response.status, response.statusText);

      if (!response.ok) {
        let errorText = '';
        let errorData = null;
        
        try {
          errorText = await response.text();
          // 嘗試解析為 JSON
          try {
            errorData = JSON.parse(errorText);
          } catch {
            // 如果不是 JSON，使用原始文本
            errorData = { error: errorText };
          }
        } catch (e) {
          errorText = '無法讀取錯誤響應';
          errorData = { error: errorText };
        }
        
        // 構建詳細的錯誤消息
        let errorMessage = `HTTP ${response.status}: `;
        if (errorData?.error) {
          errorMessage += errorData.error;
        } else if (errorData?.message) {
          errorMessage += errorData.message;
        } else {
          errorMessage += errorText;
        }
        
        // 如果有詳細信息，添加到消息中
        if (errorData?.details) {
          console.error('❌ 詳細錯誤信息:', errorData.details);
        }
        
        // 如果有請求 JSON，記錄以便調試
        if (errorData?.requestJson) {
          console.error('❌ 失敗的請求 JSON:', errorData.requestJson);
        }
        
        // 顯示詳細錯誤
        message.error({
          content: errorMessage,
          duration: 10, // 顯示 10 秒
          style: { whiteSpace: 'pre-wrap' } // 允許換行
        });
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('📥 解析響應:', result);

      if (result && result.id) {
        setIsEditing(true);
        setFormId(result.id);
        if (result.metaFlowId) {
          setMetaFlowId(result.metaFlowId);
        }
        
        message.success('✅ 表單保存成功！');
        
        // ✅ 如果用戶選擇保存為 Flow Template，則創建或更新 Flow Template
        if (saveAsTemplate && result.metaFlowId) {
          try {
            console.log('📝 檢查是否需要創建/更新 Flow Template...');
            console.log('📝 saveAsTemplate:', saveAsTemplate);
            console.log('📝 result.metaFlowId:', result.metaFlowId);
            console.log('📝 result.metaFlowTemplateId:', result.metaFlowTemplateId);
            
            // 如果已有 Template ID，使用 ForceCreate 強制重新創建（因為 Template 的 flow_action 是寫死的，需要重新創建才能更新）
            const templateResponse = await fetch(`/api/eforms/${result.id}/create-flow-template`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                flowName: flowName,
                category: 'LEAD_GENERATION',
                language: 'zh_TW',
                forceCreate: true // 強制重新創建，即使已存在（因為 Template 的 flow_action 需要更新）
              })
            });
            
            if (templateResponse.ok) {
              const templateResult = await templateResponse.json();
              if (templateResult.flowTemplateId) {
                console.log('✅ Flow Template 創建/更新成功:', templateResult.flowTemplateId);
                message.success(`✅ Flow Template 創建/更新成功！Template ID: ${templateResult.flowTemplateId}`);
              }
            } else {
              const errorData = await templateResponse.json().catch(() => ({ error: '未知錯誤' }));
              console.warn('⚠️ Flow Template 創建失敗:', errorData);
              message.warning('⚠️ Flow Template 創建失敗，但 Flow 已保存成功。您可以稍後手動創建。');
            }
          } catch (error) {
            console.error('❌ 創建 Flow Template 時發生錯誤:', error);
            message.warning('⚠️ Flow Template 創建失敗，但 Flow 已保存成功。您可以稍後手動創建。');
          }
        } else if (!saveAsTemplate) {
          console.log('ℹ️ 用戶未勾選「保存為 Flow Template」');
        } else if (!result.metaFlowId) {
          console.warn('⚠️ 無法創建 Flow Template：Flow ID 不存在');
        }
        
        onSave && onSave();
      } else {
        message.error('❌ 保存失敗: 響應格式錯誤');
      }
    } catch (error) {
      console.error('❌ 保存錯誤:', error);
      message.error('❌ 保存失敗: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAiSuccess = (flowJson, newFlowName) => {
    try {
      const jsonStr = typeof flowJson === 'string' ? flowJson : JSON.stringify(flowJson);
      const parsed = parseMetaFlowJson(jsonStr);
      const validation = validateMetaFlowJson(jsonStr);
      if (!validation.valid) {
        console.warn('Meta Flow JSON validation warnings:', validation.errors);
      }
      if (newFlowName) {
        setFlowName(newFlowName);
      }
      setFlowData(prev => ({
        ...prev,
        name: newFlowName || parsed.name || prev.name,
        categories: parsed.categories?.length ? parsed.categories : prev.categories,
        screens: parsed.screens?.length ? parsed.screens : prev.screens
      }));
      if (parsed.screens?.length) {
        setSelectedScreen(parsed.screens[0]);
        setSelectedComponent(null);
      }
    } catch (error) {
      console.error('Failed to apply AI Meta Flow JSON:', error);
      message.error((t('metaFlowBuilder.aiAssistant.applyFailed') || '套用 AI 生成的表單失敗') + ': ' + error.message);
    }
  };

  const currentFlowJsonForAi = JSON.stringify(generateMetaFlowJson(flowData, t), null, 2);

  // 添加新 Screen
  const handleAddScreen = () => {
    const newScreen = getDefaultScreen();
    setFlowData(prev => ({
      ...prev,
      screens: [...prev.screens, newScreen]
    }));
    setSelectedScreen(newScreen);
    message.success(t('metaFlowBuilder.page.messages.screenAdded'));
  };

  // 更新 Screen
  const handleUpdateScreen = (updatedScreen) => {
    setSelectedScreen(updatedScreen);
    setFlowData(prev => ({
      ...prev,
      screens: prev.screens.map(s => 
        s.id === updatedScreen.id ? updatedScreen : s
      )
    }));
  };

  // 添加組件到當前 Screen
  const handleAddComponent = (componentType) => {
    if (!selectedScreen) {
      message.warning(t('metaFlowBuilder.page.messages.selectScreenFirst'));
      return;
    }
    // 傳入現有組件列表，以便生成唯一的 name
    const currentActions = selectedScreen.data?.actions || [];
    const newComponent = getDefaultComponent(componentType, null, currentActions);
    const updatedScreen = {
      ...selectedScreen,
      data: {
        ...selectedScreen.data,
        actions: [...currentActions, newComponent]
      }
    };
    handleUpdateScreen(updatedScreen);
    message.success(`已添加 ${componentType} 組件`);
  };

  // 刪除 Screen
  const handleDeleteScreen = (screenId) => {
    setFlowData(prev => ({
      ...prev,
      screens: prev.screens.filter(s => s.id !== screenId)
    }));
    if (selectedScreen?.id === screenId) {
      setSelectedScreen(null);
    }
    message.success('已刪除 Screen');
  };

  // 如果正在載入，顯示載入狀態
  if (isLoading) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div style={{ fontSize: '18px', color: '#666' }}>載入表單內容中...</div>
        <div style={{ fontSize: '14px', color: '#999' }}>請稍候</div>
      </div>
    );
  }

  // 容器樣式 - 使用 100% 寬度適應父容器（父容器已經處理了側邊欄的 margin）
  const containerStyle = {
    height: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  };
  
  return (
    <div style={containerStyle}>
      {/* 樣式 */}
      <style>{purpleButtonStyle}</style>
      
      {/* 頂部工具欄 */}
      <div style={{ 
        padding: '16px', 
        borderBottom: '1px solid #e8e8e8', 
        backgroundColor: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            className="purple-back-button"
            style={{ 
              backgroundColor: '#722ed1', 
              borderColor: '#722ed1',
              color: 'white',
              height: '32px',
              width: '32px',
              padding: '0'
            }}
          />
          <Button
            icon={<SaveOutlined />}
            type="primary"
            onClick={handleSave}
            loading={isSaving}
            style={{
              height: '32px',
              width: '32px',
              padding: '0'
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Checkbox
              checked={saveAsTemplate}
              onChange={(e) => setSaveAsTemplate(e.target.checked)}
            >
              保存為 Flow Template
            </Checkbox>
            <Tooltip 
              title="Flow Template 可以在 24 小時窗口外發送消息。如果未勾選，將使用直接 Flow 發送（僅在 24 小時窗口內有效）。"
              placement="bottom"
            >
              <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
            </Tooltip>
          </div>
          <Button
            icon={<SettingOutlined />}
            type="default"
            onClick={openFieldDisplaySetting}
            disabled={!(flowData.screens || []).some(s => (s.data?.actions || []).length > 0)}
            style={{
              height: '32px',
              width: '32px',
              padding: '0'
            }}
            title={t('metaFlowBuilder.page.fieldDisplaySetting') || '字段顯示設定'}
          />
          <Button
            icon={<EyeOutlined />}
            type="default"
            onClick={() => setJsonPreviewVisible(!jsonPreviewVisible)}
            style={{
              height: '32px',
              width: '32px',
              padding: '0'
            }}
            title="JSON 預覽"
          />
        </div>
        
        <Title level={4} style={{ margin: 0 }}>Meta Flow Builder</Title>
      </div>

      {/* 主要內容區域 */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* 左側面板 - Screen 列表和組件庫 */}
        <div style={{ 
          width: '250px', 
          borderRight: '1px solid #e8e8e8',
          backgroundColor: '#fafafa',
          padding: '16px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          {/* 表單信息 / AI 助理 Tabs */}
          <Tabs
            activeKey={leftPanelTab}
            onChange={setLeftPanelTab}
            size="small"
            items={[
              {
                key: 'formInfo',
                label: (
                  <span>
                    <FormOutlined style={{ marginRight: 4 }} />
                    {t('metaFlowBuilder.page.formInfo')}
                  </span>
                ),
                children: (
                  <div>
                    <div style={{ marginBottom: '12px' }}>
                      <label>{t('metaFlowBuilder.page.flowName')}</label>
                      <Input
                        value={flowName}
                        onChange={(e) => {
                          setFlowName(e.target.value);
                          setFlowData(prev => ({ ...prev, name: e.target.value }));
                        }}
                        placeholder={t('metaFlowBuilder.page.flowNamePlaceholder')}
                        style={{ marginTop: '4px' }}
                      />
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <label>{t('metaFlowBuilder.page.description')}</label>
                      <TextArea
                        value={flowDescription}
                        onChange={(e) => setFlowDescription(e.target.value)}
                        placeholder={t('metaFlowBuilder.page.descriptionPlaceholder')}
                        rows={3}
                        style={{ marginTop: '4px' }}
                      />
                    </div>
                  </div>
                )
              },
              {
                key: 'aiAssistant',
                label: (
                  <span>
                    <RobotOutlined style={{ marginRight: 4 }} />
                    {t('metaFlowBuilder.aiAssistant.tabTitle') || 'AI 助理'}
                  </span>
                ),
                children: (
                  <MetaFlowBuilderAIChat
                    eFormDefinitionId={formId}
                    currentFlowJson={currentFlowJsonForAi}
                    onSuccess={handleAiSuccess}
                    isGenerating={isAiGenerating}
                    setIsGenerating={setIsAiGenerating}
                  />
                )
              }
            ]}
          />

          {/* Screen 列表 */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0 }}>{t('metaFlowBuilder.page.screens')}</h4>
              <Button
                icon={<PlusOutlined />}
                size="small"
                onClick={handleAddScreen}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {flowData.screens.map((screen, index) => (
                <Card
                  key={screen.id}
                  size="small"
                  hoverable
                  onClick={() => {
                    // 從 flowData 中獲取最新的 screen 數據
                    const latestScreen = flowData.screens.find(s => s.id === screen.id);
                    setSelectedScreen(latestScreen || screen);
                  }}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedScreen?.id === screen.id ? '#e6f7ff' : 'white',
                    border: selectedScreen?.id === screen.id ? '2px solid #1890ff' : '1px solid #d9d9d9'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{getScreenDisplayTitle(screen) || `Screen ${index + 1}`}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{screen.id}</div>
                    </div>
                    <Button
                      size="small"
                      danger
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteScreen(screen.id);
                      }}
                    >
                      {t('metaFlowBuilder.page.delete')}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* 組件庫 */}
          <ComponentPalette onAddComponent={handleAddComponent} />
        </div>

        {/* 中間畫布區域 */}
        <div style={{ 
          flex: 1, 
          position: 'relative',
          backgroundColor: '#f5f5f5',
          padding: '20px',
          overflow: 'auto'
        }}>
          <AIGeneratingOverlay visible={isAiGenerating} t={t} />
          {selectedScreen ? (
            <Card title={t('metaFlowBuilder.page.editScreen', { title: getScreenDisplayTitle(selectedScreen) || selectedScreen.id })}>
              <ScreenEditor
                screen={selectedScreen}
                onUpdate={handleUpdateScreen}
                onComponentSelect={(component) => {
                  setSelectedComponent(component);
                }}
                allScreens={flowData.screens}
              />
            </Card>
          ) : (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              color: '#999'
            }}>
              {t('metaFlowBuilder.page.selectScreenToEdit')}
            </div>
          )}
        </div>

        {/* 右側屬性編輯面板 */}
        {selectedScreen && (
          <div style={{
            width: '340px',
            borderLeft: '1px solid #e8e8e8',
            backgroundColor: 'white',
            overflowY: 'auto',
            padding: '20px'
          }}>
            {selectedComponent ? (
              <>
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>{t('metaFlowBuilder.page.componentProperties')}</h3>
                  <Button size="small" onClick={() => setSelectedComponent(null)}>{t('metaFlowBuilder.page.back')}</Button>
                </div>
                <ComponentPropertyEditor
                  component={selectedComponent}
                  onUpdate={(updates) => {
                    // 更新組件
                    const updatedComponent = { ...selectedComponent, ...updates };
                    setSelectedComponent(updatedComponent);
                    
                    // 以 isSameFlowAction 對齊：避免 (comp.id||name) 與 (sel.id||name) 混用時匹配失敗
                    const updatedScreen = {
                      ...selectedScreen,
                      data: {
                        ...selectedScreen.data,
                        actions: (selectedScreen.data?.actions || []).map(comp =>
                          isSameFlowAction(comp, selectedComponent) ? updatedComponent : comp
                        )
                      }
                    };
                    
                    handleUpdateScreen(updatedScreen);
                    message.success(t('metaFlowBuilder.page.messages.componentUpdated'));
                  }}
                  screenId={selectedScreen.id}
                  allScreens={flowData.screens}
                />
              </>
            ) : (
              <>
                <h3>{t('metaFlowBuilder.page.screenProperties')}</h3>
                <div style={{ marginBottom: '16px' }}>
                  <label>{t('metaFlowBuilder.page.screenId')}</label>
                  <Input
                    value={selectedScreen.id}
                    disabled
                    style={{ marginTop: '4px' }}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label>{t('metaFlowBuilder.page.screenTitle')}</label>
                  <Input
                    value={selectedScreen.title && selectedScreen.title !== '新 Screen' ? selectedScreen.title : ''}
                    onChange={(e) => {
                      handleUpdateScreen({
                        ...selectedScreen,
                        title: e.target.value || '新 Screen'
                      });
                    }}
                    placeholder={t('metaFlowBuilder.page.defaultScreenTitle')}
                    style={{ marginTop: '4px' }}
                  />
                </div>
                <div style={{ marginTop: '24px' }}>
                  <h4>{t('metaFlowBuilder.page.componentList')}</h4>
                  {selectedScreen.data?.actions && selectedScreen.data.actions.length > 0 ? (
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      {selectedScreen.data.actions.map((comp, index) => {
                        // 從 flowData 中獲取最新的組件數據
                        const latestScreen = flowData.screens.find(s => s.id === selectedScreen.id);
                        const latestComponent = latestScreen?.data?.actions?.find(c => 
                          isSameFlowAction(c, comp)
                        ) || comp;
                        return (
                        <Card
                          key={comp.id || comp.name || `comp_${index}`}
                          size="small"
                          hoverable
                          onClick={() => setSelectedComponent(latestComponent)}
                          style={{ 
                            cursor: 'pointer',
                            border: selectedComponent && isSameFlowAction(comp, selectedComponent)
                              ? '2px solid #1890ff' 
                              : '1px solid #d9d9d9'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ fontWeight: 'bold' }}>{getComponentDisplayTitle(comp) || t('metaFlowBuilder.page.defaultComponentName', { index: index + 1 })}</div>
                              <div style={{ fontSize: '12px', color: '#666' }}>{comp.type}</div>
                            </div>
                            <Button
                              size="small"
                              danger
                              onClick={(e) => {
                                e.stopPropagation();
                                const updatedActions = selectedScreen.data.actions.filter(c => 
                                  !isSameFlowAction(c, comp)
                                );
                                handleUpdateScreen({
                                  ...selectedScreen,
                                  data: {
                                    ...selectedScreen.data,
                                    actions: updatedActions
                                  }
                                });
                              }}
                            >
                              {t('metaFlowBuilder.page.delete')}
                            </Button>
                          </div>
                        </Card>
                        );
                      })}
                    </Space>
                  ) : (
                    <p style={{ color: '#999', fontSize: '12px' }}>{t('metaFlowBuilder.page.noComponents')}</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* JSON 預覽面板（底部） */}
      {jsonPreviewVisible && (
        <div style={{
          height: '300px',
          borderTop: '1px solid #e8e8e8',
          backgroundColor: '#fafafa',
          padding: '16px',
          overflow: 'auto'
        }}>
          <h4>JSON 預覽</h4>
          <pre style={{ 
            backgroundColor: 'white', 
            padding: '12px', 
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '12px'
          }}>
            {JSON.stringify(generateMetaFlowJson(flowData, t), null, 2)}
          </pre>
        </div>
      )}

      {/* 字段顯示設定 Modal（與 EFormDesigner 共用 eFormDefinition.field_display_settings 欄位） */}
      <MetaFlowFieldDisplaySetting
        visible={fieldDisplaySettingVisible}
        onClose={() => setFieldDisplaySettingVisible(false)}
        screens={flowData.screens}
        onSave={handleFieldDisplaySettingSave}
        initialSettings={fieldDisplaySettings}
      />
    </div>
  );
};

export default MetaFlowBuilder;

